import { useQmrState } from '@packages/contexts/qmrs';
import { type CVT } from '@packages/models/api/cvt';
import { Button, Typography } from '@packages/ui/shared';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { DiagnosticCardGroup } from './diagnostic-card-group';

type ChangeDiagnosticModalProps = {
    /** The list of CVT diagnostics available for selection. */
    diagnosticOptions: CVT[];

    /** Whether the list's first page of diagnostics failed to load. */
    hasFailedToLoadDiagnostics: boolean;

    /** Whether the list's next page of diagnostics failed to load. */
    hasFailedToLoadMoreDiagnostics: boolean;

    /** Whether the list has more pages of diagnostics to load. */
    hasMoreDiagnostics: boolean;

    /** Whether the list's first page of diagnostics is loading. */
    isLoadingDiagnostics: boolean;

    /** Whether the list's next page of diagnostics is loading. */
    isLoadingMoreDiagnostics: boolean;

    /** Whether the CVT challenge is being saved, which disables "Change Test", since a change deletes the challenge. */
    isSavingCvtChallengeDetails: boolean;

    /** Whether the report's diagnostic is being updated, which locks the modal. */
    isUpdatingDiagnostic: boolean;

    /** Callback to update the report's diagnostic to the selected one. Rejects with why it failed. */
    onChangeDiagnostic: (diagnosticId: string) => Promise<void>;

    /** Callback to hide the modal. */
    onHide: () => void;

    /** Callback to load the list's next page of diagnostics. */
    onLoadMoreDiagnostics: () => void;

    /** Callback to reload the list of diagnostics. */
    onReloadDiagnostics: () => void;

    /** Callback for the "Run New Test" button. */
    onRunNewDiagnostic: () => void;

    /** Whether the modal is open. */
    show: boolean;
};

/**
 * The modal for choosing the report's CVT diagnostic.
 *
 * Renders the diagnostics it's given as radio cards, with the attached one selected until the technician picks another.
 */
export function ChangeDiagnosticModal({
    diagnosticOptions,
    hasFailedToLoadDiagnostics,
    hasFailedToLoadMoreDiagnostics,
    hasMoreDiagnostics,
    isLoadingDiagnostics,
    isLoadingMoreDiagnostics,
    isSavingCvtChallengeDetails,
    isUpdatingDiagnostic,
    onChangeDiagnostic,
    onHide,
    onLoadMoreDiagnostics,
    onReloadDiagnostics,
    onRunNewDiagnostic,
    show,
}: ChangeDiagnosticModalProps) {
    const { t } = useTranslation();
    const { qmr } = useQmrState();

    const [changeErrorMessage, setChangeErrorMessage] = useState<string>('');
    const [lastSelectedDiagnosticId, setLastSelectedDiagnosticId] = useState<string>();

    const selectedDiagnosticId = lastSelectedDiagnosticId ?? qmr?.cvtDiagnosticsJobId;
    const isSelectedDiagnosticAvailable = diagnosticOptions.some(
        ({ diagnosticId }) => diagnosticId === selectedDiagnosticId
    );

    const isChangeDiagnosticDisabled =
        hasFailedToLoadDiagnostics ||
        isLoadingDiagnostics ||
        isSavingCvtChallengeDetails ||
        isUpdatingDiagnostic ||
        !isSelectedDiagnosticAvailable ||
        selectedDiagnosticId === qmr?.cvtDiagnosticsJobId;

    function handleSelectDiagnostic(diagnosticId: string) {
        setLastSelectedDiagnosticId(diagnosticId);
    }

    async function handleChangeDiagnosticButtonPress() {
        if (!selectedDiagnosticId || isUpdatingDiagnostic) {
            return;
        }

        setChangeErrorMessage('');

        try {
            await onChangeDiagnostic(selectedDiagnosticId);
        } catch (error) {
            setChangeErrorMessage(
                error?.message ||
                    t(
                        'qmr:sections.failCode.cvtDiagnostic.changeModal.changeError',
                        "We couldn't change the test. Please try again."
                    )
            );
        }
    }

    /** Resets once the modal has closed, out of sight, so the next open starts on the attached diagnostic. */
    function handleExited() {
        setChangeErrorMessage('');
        setLastSelectedDiagnosticId(undefined);
    }

    return (
        <Modal
            backdrop={isUpdatingDiagnostic ? 'static' : true}
            centered
            keyboard={!isUpdatingDiagnostic}
            onExited={handleExited}
            onHide={onHide}
            show={show}
            size="lg"
        >
            <Modal.Header closeButton={!isUpdatingDiagnostic}>
                <Modal.Title>
                    {t('qmr:sections.failCode.cvtDiagnostic.changeModal.title', 'Select a CVT Diagnostic Test')}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <DiagnosticCardGroup
                    diagnosticOptions={diagnosticOptions}
                    hasError={hasFailedToLoadDiagnostics}
                    hasFailedToLoadMore={hasFailedToLoadMoreDiagnostics}
                    hasMore={hasMoreDiagnostics}
                    isDisabled={isUpdatingDiagnostic}
                    isLoading={isLoadingDiagnostics}
                    isLoadingMore={isLoadingMoreDiagnostics}
                    onLoadMore={onLoadMoreDiagnostics}
                    onReload={onReloadDiagnostics}
                    onSelect={handleSelectDiagnostic}
                    selectedDiagnosticId={selectedDiagnosticId}
                />

                {changeErrorMessage && (
                    <div className="mt-2">
                        <Typography color="redOne" variant="caption">
                            {changeErrorMessage}
                        </Typography>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between align-items-center w-100">
                    <Button disabled={isUpdatingDiagnostic} onPress={onHide} variant="ghost-blue">
                        {t('buttons:cancel')}
                    </Button>
                    <div className="d-flex gap-2">
                        <Button
                            disabled={isUpdatingDiagnostic}
                            onPress={onRunNewDiagnostic}
                            variant="primary-blue-outline"
                        >
                            {t('qmr:sections.failCode.cvtDiagnostic.runNewDiagnostic', 'Run New Test')}
                        </Button>

                        <Button
                            disabled={isChangeDiagnosticDisabled}
                            isLoading={isUpdatingDiagnostic}
                            onPress={handleChangeDiagnosticButtonPress}
                            variant="primary"
                        >
                            {t('qmr:sections.failCode.cvtDiagnostic.changeDiagnostic', 'Change Test')}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
